import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Clock, Users, Euro } from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";

/**
 * Individual event permalink — /evenements/{id}.
 *
 * Why this page exists (2 reasons):
 * 1. SEO + social sharing: `/evenements` (list page) has generic OG tags — FB /
 *    Twitter / LinkedIn all display the site logo. This page injects PER-EVENT
 *    Open Graph tags (title, description, image) so shares carry the event's
 *    own visual and copy. Each URL is prerendered independently.
 * 2. Real "product page" for the atelier — full description accessible from a
 *    permalink, with an actual "Réserver ma place" button that opens the same
 *    payment popup as the list card (Stripe / PayPal / virement / code cadeau).
 */
export default function EventDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [dialog, setDialog] = useState(null);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const { data } = await http.get(`/events/${id}`);
                if (!cancel) setEvent(data);
            } catch (e) {
                if (!cancel && e?.response?.status === 404) setNotFound(true);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [id]);

    // Same shape as Events.jsx::handleRegister — decides free vs paid via the
    // backend, then either short-circuits (subscription / already-registered)
    // or opens the payment dialog.
    const handleRegister = async () => {
        if (!user || user === false) {
            toast.info("Connectez-vous pour vous inscrire.");
            navigate("/connexion");
            return;
        }
        try {
            const reg = await http.post(`/events/${event.id}/register`, {});
            if (reg.data.free) {
                if (reg.data.status === "already_registered") {
                    toast.warning(
                        "Vous êtes déjà inscrit à cet évènement — pour toute question en lien avec l'atelier à venir (lien vers l'évènement ou autres), veuillez nous écrire à reservation@foundation-of-light.com",
                        { duration: 12000 }
                    );
                } else if (reg.data.free_reason === "free_event") {
                    toast.success(
                        "Merci d'apporter votre lumière à cet évènement ✨ Un email de confirmation vient de partir avec le lien de connexion et les liens d'ajout à vos calendriers — à très bientôt.",
                        { duration: 14000 }
                    );
                } else {
                    toast.success(
                        "Inscription automatique grâce à votre abonnement — vous recevrez le lien 3 jours avant la date de l'évènement. Assurez-vous que l'adresse reservation@foundation-of-light.com est bien enregistrée dans vos contacts pour éviter que l'email avec le lien soit routé vers les courriers indésirables (spam).",
                        { duration: 14000 }
                    );
                }
                return;
            }
            const priceCents = Math.round((reg.data.price || event.price) * 100);
            setDialog({
                kind: "event",
                event_id: event.id,
                item_name: `Événement : ${event.title}`,
                amount: priceCents,
                origin_url: window.location.origin,
            });
        } catch (e) {
            const isDup = e?.response?.status === 409;
            toast.error(formatApiError(e), { duration: isDup ? 15000 : 6000 });
        }
    };

    // Same 2 branches as Events.jsx::stripeCheckout — with `discount_code` we hit
    // the register endpoint directly for a free reg; without, we go to Stripe.
    const stripeCheckout = async (opts) => {
        if (opts?.discount_code) {
            const { data } = await http.post(`/events/${dialog.event_id}/register`, {
                discount_code: opts.discount_code,
            });
            if (data.free) {
                toast.success(`🎁 Atelier offert grâce au code ${opts.discount_code} — confirmation envoyée par email`);
                setDialog(null);
                return;
            }
        }
        const { data } = await http.post("/payments/checkout", {
            event_id: dialog.event_id,
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    if (loading) {
        return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground">Chargement…</div>;
    }
    if (notFound || !event) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="event-not-found">
                <Seo title="Événement introuvable · Foundation of Light" description="Cet événement n'existe pas ou a été retiré." path="" noindex />
                <h1 className="font-serif text-4xl mb-4">Événement introuvable</h1>
                <Link to="/evenements"><Button className="rounded-full mt-6">Voir tous les ateliers</Button></Link>
            </div>
        );
    }

    const when = new Date(event.starts_at);
    const whenFR = when.toLocaleDateString("fr-FR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
    const priceText = event.price > 0 ? `${event.price} €` : "Gratuit";
    const ogImage = event.image_url || "https://foundation-of-light.com/brand/logo.png";

    return (
        <article className="max-w-3xl mx-auto px-6 py-16" data-testid={`event-detail-${event.id}`}>
            <Seo
                title={`${event.title} · Foundation of Light`}
                description={event.description || "Un atelier de langage de lumière avec Foundation of Light."}
                path={`/evenements/${event.id}`}
                image={ogImage}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Event",
                    "name": event.title,
                    "startDate": event.starts_at,
                    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
                    "eventStatus": "https://schema.org/EventScheduled",
                    "location": {
                        "@type": "VirtualLocation",
                        "url": `https://foundation-of-light.com/evenements/${event.id}`,
                    },
                    "image": ogImage,
                    "description": event.description || undefined,
                    "offers": {
                        "@type": "Offer",
                        "price": event.price || 0,
                        "priceCurrency": "EUR",
                        "url": `https://foundation-of-light.com/evenements/${event.id}`,
                    },
                    "organizer": {
                        "@type": "Organization",
                        "name": "Foundation of Light",
                        "url": "https://foundation-of-light.com",
                    },
                }}
            />

            <Link to="/evenements" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="back-to-events">
                <ArrowLeft className="w-4 h-4" /> Tous les ateliers
            </Link>

            {event.image_url && (
                <img
                    src={event.image_url}
                    alt={event.image_alt || event.title}
                    className="w-full h-64 md:h-80 object-cover rounded-3xl mb-8"
                />
            )}

            <div className="text-xs uppercase tracking-widest text-primary mb-3">
                {event.category}{event.series ? ` · série ${event.series}` : ""}
            </div>
            <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-6">{event.title}</h1>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-8 border-y border-border/40 py-4">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-primary" />{whenFR}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" />{event.duration_minutes} min</span>
                <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4 text-primary" />{event.registered_count || 0} / {event.capacity} inscrits</span>
                <span className="inline-flex items-center gap-1.5"><Euro className="w-4 h-4 text-primary" />{priceText}</span>
            </div>

            {event.description && (
                <div className="prose prose-lg max-w-none text-foreground/85 leading-relaxed whitespace-pre-wrap mb-10">
                    {event.description}
                </div>
            )}

            <Button
                size="lg"
                className="rounded-full bg-primary text-primary-foreground"
                onClick={handleRegister}
                data-testid="event-detail-reserve"
            >
                Réserver ma place →
            </Button>

            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={stripeCheckout}
                allowGiftCode={true}
                giftCodeScope="event"
            />
        </article>
    );
}
